import { useCallback, useState } from "react";
import { TFunction } from "i18next";
import { ValidationError } from "src/models/validation-error";
import { getTranslatedErrorMsg } from "src/common/util";

/**
 * A deferred, translatable message.
 *
 * Components frequently need to keep a user-facing message in React state (an
 * inline error, a field status, a banner subheader). Storing the already
 * translated string freezes it in whatever language was active when it was set,
 * so it does not update when the user changes language. Storing an
 * {@link I18nMessage} descriptor instead lets the message be resolved through
 * `t()` at render time, keeping it in sync with the active language like every
 * other translated label.
 *
 *   - 'key':        an i18n key with optional named interpolation params.
 *   - 'validation': a structured {@link ValidationError} translated via getTranslatedErrorMsg.
 *   - 'raw':        a server/network string that is not localizable and is shown as-is.
 */
export type I18nMessage =
    | { kind: 'key'; key: string; params?: Record<string, unknown> }
    | { kind: 'validation'; error: ValidationError }
    | { kind: 'raw'; text: string };

/** Build a key-based {@link I18nMessage}. */
export const i18nKey = (key: string, params?: Record<string, unknown>): I18nMessage =>
    ({ kind: 'key', key, params });

/** Build a {@link I18nMessage} from a structured backend {@link ValidationError}. */
export const i18nValidation = (error: ValidationError): I18nMessage =>
    ({ kind: 'validation', error });

/** Build a raw (non-localizable) {@link I18nMessage}. */
export const i18nRaw = (text: string): I18nMessage =>
    ({ kind: 'raw', text });

/**
 * Resolve an {@link I18nMessage} to a display string using the given translation
 * function. Call this at render time so a language change re-resolves the
 * message. Returns null for a null/undefined message so callers can guard
 * rendering with a simple truthiness check.
 */
export const resolveI18nMessage = (t: TFunction, message: I18nMessage | null | undefined): string | null => {
    if (!message) {
        return null;
    }
    switch (message.kind) {
        case 'key':
            return t(message.key, message.params);
        case 'validation':
            return getTranslatedErrorMsg(t, message.error);
        case 'raw':
            return message.text;
    }
};

/**
 * State hook for a single deferred, translatable message. Stores an
 * {@link I18nMessage} descriptor and exposes setters that keep call sites
 * readable.
 *
 * Resolve the returned `message` with {@link resolveI18nMessage} at render time.
 */
export const useI18nMessage = (initial: I18nMessage | null = null) => {
    const [message, setMessage] = useState<I18nMessage | null>(initial);

    const setKey = useCallback(
        (key: string, params?: Record<string, unknown>) => setMessage(i18nKey(key, params)),
        [],
    );
    const setValidation = useCallback((error: ValidationError) => setMessage(i18nValidation(error)), []);
    const setRaw = useCallback((text: string) => setMessage(i18nRaw(text)), []);
    const clear = useCallback(() => setMessage(null), []);

    return { message, setMessage, setKey, setValidation, setRaw, clear };
};
